import { Box, Button, Grid, Tooltip, Typography } from "@mui/material";
import Table, { ExtendedColumnType } from "components/Table";
import _ from "lodash";
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { ResType } from "types";
import { useSelector } from "react-redux";
import {
    createGstrPromiseClient,
    getGstrRecords,
    exportGstr9,
} from "api/services/clients/gstr-clients";
import { snack } from "components/toast";
import React from "react";
import {
    RegularTaxHeadings,
    SortDirection,
} from "data/constants";
import { getGstFinancialYears } from "utils/FinancialYears";
import { selectGstRegister } from "redux/reducers/gstRegisterSlice";
import AppliedFilters from "views/register/ApplidedFilters";
import PeriodSelect from "../Gstr-4/PeriodSelect";
import { useLocation } from "react-router-dom";
import sortHandler from "components/Table/SortHandler";
import SyncAtomPro from "views/tasks/board/SyncAtomPro";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import GstReturnsTracker from "views/register/Filter";
import excelIcon from "./../../../../../../assets/images/128px-Microsoft_Office_Excel_(2019–2025).svg.png";
import { getCurrentFormattedDateTime } from "utils";
import { handleError } from "utils/handleError";
import { GST_STATUS_STYLE, getDueDate, resolveGstStatus } from "utils/gstStatus";
import { useSelectionSync } from "views/register/GstReturns/design/ToolbarSlot";
import { removeGstrRegistrations } from "api/services/clients/gstr-clients";
import { useConfirm } from "context/ConfirmDialog";

const RTNTYPE = "GSTR9";

function Gstr9Task() {
    const confirm = useConfirm();
    const [selected, setSelected] = useState<any[]>([]);
    const financialYears = getGstFinancialYears();
    const [selectedYear, setSelectedYear] = React.useState(financialYears[1]);
    const [page, setPage] = useState<number>(0);
    const [pageCount, setPageCount] = useState<number>(10);
    const [filters, setFilters] = useState({
        labels: [],
        search: "",
    });
    const [tableData, setTableData] = useState<any[]>([]);
    const [lodd, setLoad] = useState(false);
    const [openFilter, setOpenFilter] = useState<boolean>(false);
    const queryClient = useQueryClient();
    const states = useSelector(selectGstRegister);
    const location = useLocation();
    const state: any = location.state;
    const [sort, setSort] = useState<any>({});
    const [downloading, setDownloading] = useState(false);

    useEffect(() => {
        setSelected([]);
    }, [selectedYear]);

    const queryParams = {
        limit: pageCount,
        offset: page * pageCount,
        query: {
            ...filters,
            labels: filters.labels.map((c: any) => c?.id),
            sort,
        },
        type: "regular_taxpayer",
        rtntype: "GSTR9",
        selectedYear: selectedYear,
        category:
            states?.appliedFilters?.category["value"] === undefined
                ? []
                : [states?.appliedFilters?.category["value"]],
        subCategory: states?.appliedFilters?.subCategory.map((c: any) => c?.value),
        status: (states?.appliedFilters?.status || []).map((s: any) => s.value),
        jurisdiction:
            states?.appliedFilters?.jurisdiction["value"] === undefined
                ? []
                : [states?.appliedFilters?.jurisdiction["value"]],
    };

    const { data, isLoading }: ResType = useQuery(
        ["clients-gst-returns", queryParams],
        getGstrRecords,
        {
            enabled: !!selectedYear,
            onSuccess: (res: any) => {
                setTableData(res?.data?.result);
            },
            onError: (err: any) => {
                if (!err?.error) {
                    snack.error(`An error occurred while fetching register data`);
                }
            },
        }
    );

    const totalCount = data?.data?.count;

    const { mutate: exportGstr9Mutation, isLoading: isExporting } = useMutation(
        exportGstr9,
        {
            onSuccess: (res: any) => {
                const arr = new Uint8Array(res.data?.buffer?.data);

                const blob = new Blob([arr], {
                    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                });

                const file = window.URL.createObjectURL(blob);
                const formattedDateTime = getCurrentFormattedDateTime();

                const link = document.createElement("a");
                link.href = file;
                link.download =
                    res.data?.fileName || `GSTR9-${selectedYear}-${formattedDateTime}.xlsx`;
                link.click();

                snack.success("Export successful!");
            },
            onError: (err: any) => {
                snack.error("Failed to export Excel file");
                snack.error(handleError(err));
            },
        }
    );
    const handleExport = () => {
        exportGstr9Mutation({
            ...queryParams,
        });
    };

    const monthLydefaultColumns: Array<ExtendedColumnType> = [
        {
            key: "client.clientId",
            title: "Client ID",
            default: true,
            render: (row: any) =>
                row?.client ? row?.client?.clientId : row?.clientGroup?.clientId,
        },
        {
            key: "category",
            title: "Category",
            default: true,
            render: (row: any) =>row?.client?.newCategory?.name,
        },
        {
            key: "client.gstNumber",
            title: "GSTIN",
            default: true,
            render: (row: any) =>
                row?.client ? row?.client?.gstNumber : row?.clientGroup?.gstNumber,
        },
        {
            key: "client.displayName",
            title: "Client Name",
            default: true,
            render: (row: any) =>
                row?.client
                    ? row?.client?.displayName
                    : row?.clientGroup?.displayName,
        },
        {
            key: "status",
            title: "Return Status",
            default: true,
            render: (row: any) => {
                const s = GST_STATUS_STYLE[resolveGstStatus(row, getDueDate(RTNTYPE, selectedYear, "", "month"))];
                return <Typography sx={{ color: s.ink }}>{s.label}</Typography>;
            },
        },
        {
            key: "dof",
            title: "Date of Filing",
            default: true,
            render: (row: any) =>
                row?.returnsData?.[0]?.dof ? row?.returnsData?.[0]?.dof : "-",
        },
        {
            key: "arn",
            title: "ARN",
            default: true,
            render: (row: any) =>
                row?.returnsData?.[0]?.arn ? row?.returnsData?.[0]?.arn : "-",
        },
    ];

    const [columns, setColumns] = useState(_.cloneDeep(monthLydefaultColumns));
    const handleSort = (v: any) => {
        sortHandler({
            key: v.key,
            columns,
            sortState: sort,
            setSortState: setSort,
            setColumns: setColumns,
        });
    };

    const { mutate, isLoading: loading } = useMutation(createGstrPromiseClient, {
        onSuccess: (res) => {
            setTimeout(() => {
                queryClient.invalidateQueries("clients-gst-returns");
                setLoad(false);
                snack.success("Sync Successful");
            }, 200);
        },
        onError: (err: any) => {
            snack.error(err.response.data.message);
            setLoad(false);
        },
    });

    const getGstReturnClients = async () => {
        if (!selectedYear) return snack.error("Select Financial Year");
        if (!selected.length) return snack.error("Select At least one Client");
        let clientDetails: any = [];
        for (let i of selected) {
            // GSTR-9 is annual (one period) - only sync a client that has a
            // genuine "Not Filed" status; Filed, NA, or Not-due-yet all mean
            // there's nothing to gain from syncing.
            if (i?.returnsData?.[0]?.status !== "Not Filed") continue;
            if (i?.client?.id) {
                clientDetails.push({
                    id: i.id,
                    clientId: i.client.id,
                    gstNumber: i.client.gstNumber,
                    returnsData: i.returnsData,
                });
            } else if (i?.clientGroup?.id) {
                clientDetails.push({
                    id: i.id,
                    clientGroupId: i.clientGroup.id,
                    gstNumber: i.clientGroup.gstNumber,
                    returnsData: i.returnsData,
                });
            }
        }
        if (!clientDetails.length) {
            return snack.error(
                "One-Time Sync! Your GST Returns data has been synced and is ready to use. Since there's no option to revise past GST returns, your data will stay synced."
            );
        }
        setLoad(true);
        mutate({
            financialYear: selectedYear,
            type: 'regular_taxpayer',
            clientDetails,
        });
    };

    const { mutate: removeMutate, isLoading: removing } = useMutation(
        () => removeGstrRegistrations(selected.map((r: any) => r.id)),
        {
            onSuccess: () => {
                snack.success("Removed from tracking");
                setSelected([]);
                queryClient.invalidateQueries("clients-gst-returns");
            },
            onError: (err: any) => snack.error(err?.response?.data?.message || "Failed to remove from tracking"),
        }
    );

    const handleRemove = () => {
        confirm({
            msg: `Would you like to remove these Client(s) from GST Return Tracking? Their filing history will remain intact, but they will no longer appear on this list. You can re-add them later if needed...`,
            action: () => removeMutate(),
        });
    };

    useSelectionSync({ count: selected.length, onRemove: handleRemove, removing });

    return (
        <Box>
            <Grid
                container
                spacing={2}
                mb={2}
                alignItems="center"
                justifyContent="space-between"
            >
                {/* Period Select + Search */}
                <Grid
                    item
                    xs={12}
                    md={6}
                    display="flex"
                    flexWrap="wrap"
                    alignItems="center"
                    gap={2}
                    justifyContent={{ xs: "center", md: "flex-start" }}
                >
                    <PeriodSelect
                        selectedYear={selectedYear}
                        setSelectedYear={setSelectedYear}
                        searchValue={filters.search}
                        onSearchChange={(v) => setFilters({ ...filters, search: v })}
                    />
                </Grid>

                {/* Action Buttons */}
                <Grid
                    item
                    xs={12}
                    md={6}
                >
                    <Box
                        display="flex"
                        justifyContent={{ xs: "center", md: "flex-end" }}
                        alignItems="center"
                        flexWrap="wrap"
                        gap={2}
                    >
                        <Button
                            color="secondary"
                            variant="outlined"
                            onClick={getGstReturnClients}
                            disabled={loading || lodd}
                        >
                            {loading || lodd ? "Syncing…" : "Sync With Portal"}
                        </Button>
                        <SyncAtomPro selected={selected} />

                        <Button
                            startIcon={<FilterAltOutlinedIcon />}
                            onClick={() => setOpenFilter(true)}
                            color="primary"
                            sx={{
                                border: "1px solid lightgrey",
                                borderRadius: "4px",
                            }}
                        >
                            Filters
                        </Button>

                        <Tooltip title={downloading ? "Exporting..." : "Data Export to Excel"}>
                            <Box
                                component="img"
                                src={excelIcon}
                                alt="Export"
                                sx={{
                                    cursor: downloading ? "not-allowed" : "pointer",
                                    width: 33,
                                    height: 33,
                                    opacity: downloading ? 0.5 : 1,
                                }}
                                onClick={downloading ? undefined : handleExport}
                            />
                        </Tooltip>
                    </Box>
                </Grid>
            </Grid>


            <Box display="flex" gap={1.75} flexWrap="wrap" alignItems="center" px={2} py={1}>
                {(["filed", "miss", "notDue", "blank", "off"] as const).map((k, idx) => {
                    const s = GST_STATUS_STYLE[k];
                    return (
                        <Box key={k} display="flex" alignItems="center" gap={1.75}>
                            {idx > 0 && <Typography sx={{ color: "text.disabled" }}>|</Typography>}
                            <Tooltip title={s.tooltip}>
                                <Typography sx={{ color: s.ink, cursor: "default" }}>{s.label}</Typography>
                            </Tooltip>
                        </Box>
                    );
                })}
            </Box>

            <AppliedFilters />
            <Table
                sx={{ mt: 1 }}
                loading={isLoading || lodd}
                data={tableData || []}
                columns={columns}
                sortHandler={handleSort}
                selection={{ selected, setSelected }}
                pagination={{ totalCount, page, setPage, pageCount, setPageCount }}
                heading={RegularTaxHeadings.GSTR9}
            />

            <GstReturnsTracker open={openFilter} setOpen={setOpenFilter} />
        </Box>
    );
}

export default Gstr9Task;
